import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import Modal from "antd/lib/modal";
import Dropdown from "antd/lib/dropdown";
import Button from "antd/lib/button";
import LoadingOutlinedIcon from "@ant-design/icons/LoadingOutlined";
import EllipsisOutlinedIcon from "@ant-design/icons/EllipsisOutlined";
import PlainButton from "@/components/plain-button/PlainButton";
import { useTheme } from "@/components/ThemeProvider";
import "./MenuButton.less";

export default function MenuButton({ doDelete, doArchive, doUnarchive, archived }) {
  const [loading, setLoading] = useState(false);
  const { isDarkMode } = useTheme();

  const execute = useCallback(action => {
    setLoading(true);
    action().finally(() => {
      setLoading(false);
    });
  }, []);

  const confirmDelete = useCallback(() => {
    Modal.confirm({
      title: "Delete Event",
      content: "Are you sure you want to delete this event?",
      okText: "Delete",
      okType: "danger",
      onOk: () => execute(doDelete),
      maskClosable: true,
      autoFocusButton: null,
    });
  }, [doDelete, execute]);

  const confirmArchive = useCallback(() => {
    Modal.confirm({
      title: "Archive Event",
      content: "Are you sure you want to archive this event?",
      okText: "Archive",
      okType: "danger",
      onOk: () => execute(doArchive),
      maskClosable: true,
      autoFocusButton: null,
    });
  }, [doArchive, execute]);

  const confirmUnarchive = useCallback(() => {
    Modal.confirm({
      title: "Unarchive Event",
      content: "Are you sure you want to unarchive this event?",
      okText: "Unarchive",
      okType: "danger",
      onOk: () => execute(doUnarchive),
      maskClosable: true,
      autoFocusButton: null,
    });
  }, [doUnarchive, execute]);

  return (
    <Dropdown
      className={cx("menu-button", { "dark-mode": isDarkMode })}
      trigger="click" 
      placement="bottomRight"
      overlay={
        <ul className={cx("ant-dropdown-menu ant-dropdown-menu-root ant-dropdown-menu-vertical", { "dark-mode": isDarkMode })} role="menu" tabIndex="0">
          <li className="ant-dropdown-menu-item ant-dropdown-menu-item-only-child" role="menuitem">
            <PlainButton onClick={confirmDelete} className={cx("clickable", { "dark-mode": isDarkMode })}>Delete</PlainButton>
          </li>
          <li className="ant-dropdown-menu-item ant-dropdown-menu-item-only-child" role="menuitem">
            {archived ? (
              <PlainButton onClick={confirmUnarchive} className={cx("clickable", { "dark-mode": isDarkMode })}>Unarchive</PlainButton>
            ) : (
              <PlainButton onClick={confirmArchive} className={cx("clickable", { "dark-mode": isDarkMode })}>Archive</PlainButton>
            )}
          </li>
        </ul>
      }>
      <Button aria-label="More actions" className={cx({ "dark-mode": isDarkMode })}>
        {loading ? <LoadingOutlinedIcon /> : <EllipsisOutlinedIcon rotate={90} aria-hidden="true" />}
      </Button>
    </Dropdown>
  );
}

MenuButton.propTypes = {
  doDelete: PropTypes.func.isRequired,
  doArchive: PropTypes.func.isRequired,
  doUnarchive: PropTypes.func.isRequired,
  archived: PropTypes.bool.isRequired,
};
